// ═══════════════════════════════════════════════════════════════
// axios — client HTTP unique pour toute l'app
// ═══════════════════════════════════════════════════════════════
// Convention :
//   - baseURL : VITE_API_URL en prod (https://api.foodcostos.app)
//               '/api' en dev (proxy Vite → localhost:3001)
//   - withCredentials: true → cookie httpOnly refresh_token envoyé
//   - Authorization: Bearer <accessToken> injecté depuis le store Pinia
//   - 401 → refresh automatique + retry de la requête originale
//
// Le store Pinia auth est chargé de manière paresseuse pour briser la
// dépendance circulaire (store import api, api needs store).
// ═══════════════════════════════════════════════════════════════

import axios from 'axios';

const baseURL = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api`
  : '/api';

export const api = axios.create({
  baseURL,
  timeout: 30_000,
  withCredentials: true, // envoie/reçoit les cookies httpOnly cross-origin
});

let authStoreGetter = null;

// Appelée par main.js après création de Pinia
export function attachAuthStore(getter) {
  authStoreGetter = getter;
}

// ─── Request : injecte le Bearer + X-Brand ────────────────
// X-Brand : indispensable en dev/staging où Origin=localhost ne contient pas
// "stockzone". Le backend l'utilise dans detectBrandFromRequest pour valider
// que le compte appartient bien à la brand qui sert la requête.
import { currentBrand } from './brand';
api.interceptors.request.use((config) => {
  const auth = authStoreGetter?.();
  if (auth?.accessToken) {
    config.headers.Authorization = `Bearer ${auth.accessToken}`;
  }
  config.headers['X-Brand'] = currentBrand.code;
  // CSRF guard côté back : Origin/Referer obligatoire sur /refresh, /logout.
  // axios pose Origin automatiquement en navigateur, rien à faire ici.
  return config;
});

// Session morte (refresh impossible) : on nettoie et on REDIRIGE vers login pour
// couper la boucle de 401 (sinon les requêtes protégées — et les retries
// TanStack Query — bouclent indéfiniment sur une page cassée). Redirection dure
// (recharge complète → bootstrap propre) ; garde-fous : une seule fois, et jamais
// depuis une page publique (login/reset/invitation) pour éviter tout loop.
// /signup : inscription publique — un visiteur sans session y reçoit un 401 au
// démarrage (restauration de session) ; sans cette exemption, la page
// s'affichait puis repartait vers /login.
const PUBLIC_PATH_RE = /^\/(login|signup|reset-password|forgot-password|accept-invitation|verify-email)/;
let redirectingToLogin = false;
function endDeadSession() {
  const auth = authStoreGetter?.();
  auth?.clearSession();
  if (redirectingToLogin) return;
  const path = window.location?.pathname || '';
  if (PUBLIC_PATH_RE.test(path)) return;
  redirectingToLogin = true;
  window.location.assign('/login');
}

// Routes d'authentification PUBLIQUES : leur 401 est une vraie réponse métier
// (mauvais identifiants, code MFA faux…), pas une session expirée. Tenter un
// refresh dessus masquait l'erreur réelle derrière « Vous devez vous
// reconnecter » (ERR_UNAUTHORIZED du /refresh sans cookie) — incident VITA 28/09.
const AUTH_NO_REFRESH_RE = /\/auth\/(login|signup|mfa\/(verify|recovery|email\/verify|email\/resend)|oauth\/|forgot-password|reset-password|verify-email|resend-verification)/;
export function shouldAttemptRefresh(url) {
  return !AUTH_NO_REFRESH_RE.test(url || '');
}

// ─── Response : refresh automatique sur 401 ───────────────
// Le refresh est dédupliqué DANS le store (un seul /auth/refresh en vol, partagé
// avec bootstrap) — cf. stores/auth.js. On appelle donc simplement store.refresh()
// ici ; les 401 parallèles convergent sur la même promesse en vol.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const url = original?.url || '';

    // Pas un 401, ou 401 d'une route d'auth publique → on relance tel quel
    if (status !== 401 || !original || !shouldAttemptRefresh(url)) {
      return Promise.reject(error);
    }
    // Si c'est /auth/refresh lui-même qui a 401 → session morte
    if (url.endsWith('/auth/refresh') || original._retry) {
      endDeadSession();
      return Promise.reject(error);
    }

    original._retry = true;
    try {
      await authStoreGetter?.().refresh();
      // Remettre à jour le Bearer pour la requête originale
      const auth = authStoreGetter?.();
      if (auth?.accessToken) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${auth.accessToken}`;
      }
      return api(original);
    } catch (refreshErr) {
      endDeadSession();
      return Promise.reject(refreshErr);
    }
  },
);

// ─── Helper : extraire le code d'erreur API (ERR_*) d'une exception axios ─
export function errorCode(err) {
  return err?.response?.data?.error || 'ERR_INTERNAL';
}

export function errorDetails(err) {
  return err?.response?.data?.details || null;
}
